import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { URLS } from '../../data/navigation/navigation.data';
import { findStoreListing, storeItemName } from '../../data/product/product-lookup.data';
import { ToastService } from '../toast/toast.service';
import { CartAddResult, CartService, MAX_ITEMS, MAX_QUANTITY } from './cart.service';

/** the mini cart in the top nav, when one is on the page */
export interface MiniCartHost {
  /** shows the cart with this line highlighted; false when it cannot (the nav is hidden, e.g. on a phone) */
  showAdded( sku: string ): boolean;
}

/**
 * Add to cart, from anywhere on the site, with the feedback that fits the
 * screen (owner decision 2026-09-20: adding never leaves the page):
 *  - desktop: the mini cart drops down from the top nav with the line
 *    highlighted (MiniCartComponent), and closes by itself;
 *  - a phone (no top nav): a toast with the way to the cart;
 *  - on the cart page itself: a toast only, the line is already on the page.
 * A second click on the same listing adds a second copy.
 */
@Injectable( {
  providedIn: 'root',
} )
export class CartPopupService {

  private host: MiniCartHost | null = null;

  constructor(
    private cartService: CartService,
    private toastService: ToastService,
    private router: Router,
  ) {
  }

  attach( host: MiniCartHost ): void {
    this.host = host;
  }

  detach( host: MiniCartHost ): void {
    if ( this.host === host ) this.host = null;
  }

  /** puts the listing in the cart and shows it: the mini cart, or a toast. */
  add( sku: string, source: string ): CartAddResult {
    const result = this.cartService.add( sku, source );
    if ( result === 'invalid' ) return result;

    if ( result === 'full' ) {
      this.toastService.show( `Your cart already has ${ MAX_QUANTITY } copies of this item, the most one order can hold.` );
      return result;
    }
    if ( result === 'cart-full' ) {
      this.toastService.show( `Your cart is full: ${ MAX_ITEMS } different items. Remove one to add another.` );
      return result;
    }

    const onCartPage = this.router.url.split( /[?#]/ )[ 0 ] === URLS.cart;
    if ( !onCartPage && this.host?.showAdded( sku ) ) return result;

    const title = titleOf( sku );
    // a free listing is one copy: the second click shows the line that is there (2026-09-21)
    const message = result === 'already' ? `${ title } is already in your cart` : result === 'increased' ? `One more ${ title } in your cart` : `${ title } added to your cart`;
    if ( onCartPage ) {
      this.toastService.show( message );
      // the line sits in the panel above the suggestions: bring it into view (owner ask 2026-09-20)
      setTimeout( () => document.querySelector( `app-store-item-card[data-sku="${ sku }"]` )?.scrollIntoView( { behavior: 'smooth', block: 'center' } ), 50 );
    } else {
      this.toastService.show( message, {
        actionLabel: 'View cart',
        action: () => this.router.navigate( [ URLS.cart ] ),
        duration: 6000,
      } );
    }
    return result;
  }
}

/** "Ocean Theme for Sublime Text" from a sku, for the toasts */
export function titleOf( sku: string ): string {
  const listing = findStoreListing( sku );
  const themeName = storeItemName( listing, listing?.productKey || sku );
  return listing?.platformName ? `${ themeName } for ${ listing.platformName }` : themeName;
}
